import React, { useMemo, useState } from "react";

function questionTargetsUserPopulation(q) {
  const signals = Array.isArray(q?.signals) ? q.signals : [];
  return signals.some((s) =>
    ["X", "M", "Z", "Y"].includes(String(s || "").trim()),
  );
}

function questionPrimarySignal(q) {
  const signals = Array.isArray(q?.signals) ? q.signals : [];
  return (
    signals.find((s) =>
      ["X", "M", "Z", "Y"].includes(String(s || "").trim()),
    ) || null
  );
}

function dedupeAtomList(items) {
  const seen = new Set();
  const out = [];

  items.forEach((item) => {
    const clean = String(item || "").trim();
    if (!clean) return;

    const key = clean.toLowerCase();
    if (seen.has(key)) return;

    seen.add(key);
    out.push(clean);
  });

  return out;
}

function buildCommittedItemsWithDraft(items, draft) {
  return dedupeAtomList([
    ...(Array.isArray(items) ? items : []),
    String(draft || "").trim(),
  ]);
}

function buildCommittedItemsForQuestion(q, items, draft) {
  const committed = buildCommittedItemsWithDraft(items, draft);

  if (committed.length > 0) return committed;

  const fallbackVariable = String(q?.variable || "").trim();
  return fallbackVariable ? [fallbackVariable] : [];
}

function buildConditionalPrompt(signal) {
  if (signal === "Y") {
    return "Nominate the single outcome variable that PAT should treat as the focal outcome (Y). Only one Y can proceed.";
  }

  if (signal === "X") {
    return "List any variable, exposure, or intervention that should be treated as the primary candidate cause, exposure, condition, or intervention (X). Leave blank if the role seems plausible but you cannot yet name it.";
  }

  if (signal === "M") {
    return "List any pathway or mechanism that could plausibly link the proposed candidate X to the nominated outcome (Y). Leave blank if mediation seems plausible but you cannot yet name it.";
  }

  if (signal === "Z") {
    return "List any background condition that could plausibly influence both the proposed candidate (X) and the nominated outcome (Y). Leave blank if confounding seems plausible but you cannot yet name the variables.";
  }

  return "";
}

function isOutcomeConfirmationQuestion(q) {
  const signal = questionPrimarySignal(q);
  return signal === "Y";
}

function isPredictorConfirmationQuestion(q) {
  const signal = questionPrimarySignal(q);
  return signal === "X";
}

function isConfounderConfirmationQuestion(q) {
  const signal = questionPrimarySignal(q);
  return signal === "Z";
}

function isMediatorConfirmationQuestion(q) {
  const signal = questionPrimarySignal(q);
  return signal === "M";
}

function hasConcreteMediatorCandidate(q) {
  if (!isMediatorConfirmationQuestion(q)) return false;

  const rawVariable = String(q?.variable || "").trim();
  const variable = rawVariable.toLowerCase();

  if (!variable) return false;

  if (isSymbolicSingleLetterVariable(rawVariable)) {
    return true;
  }

  const genericMediatorPlaceholders = new Set([
    "mechanism / pathway",
    "mechanism/pathway",
    "mechanism",
    "pathway",
    "pathways",
    "mediator",
    "mediators",
    "possible mediator",
    "possible mediators",
    "candidate mediator",
    "candidate mediators",
    "intermediate pathway",
    "intermediate pathways",
    "intermediate mechanism",
    "intermediate mechanisms",
  ]);

  return !genericMediatorPlaceholders.has(variable);
}

function hasConcreteConfounderCandidate(q) {
  if (!isConfounderConfirmationQuestion(q)) return false;

  const rawVariable = String(q?.variable || "").trim();
  const variable = rawVariable.toLowerCase();

  if (!variable) return false;

  if (isSymbolicSingleLetterVariable(rawVariable)) {
    return true;
  }

  const genericConfounderPlaceholders = new Set([
    "contextual factors",
    "contextual factor",
    "background conditions",
    "background condition",
    "background factors",
    "background factor",
    "background variables",
    "background variable",
    "confounders",
    "confounder",
    "possible confounders",
    "possible confounder",
    "adjustment variables",
    "adjustment variable",
    "covariates",
    "covariate",
  ]);

  return !genericConfounderPlaceholders.has(variable);
}

function usesReplacementOnNo(q) {
  if (isOutcomeConfirmationQuestion(q)) return true;
  if (isPredictorConfirmationQuestion(q)) return true;

  // Concrete M candidates are accepted on yes and optionally replaced on no.
  // Generic M prompts require analyst nomination on yes.
  if (isMediatorConfirmationQuestion(q)) {
    return hasConcreteMediatorCandidate(q);
  }

  // Concrete Z candidates are accepted on yes and optionally replaced on no.
  // Generic Z prompts require analyst nomination on yes.
  if (isConfounderConfirmationQuestion(q)) {
    return hasConcreteConfounderCandidate(q);
  }

  return false;
}

function isSymbolicSingleLetterVariable(value) {
  const variable = String(value || "").trim();
  return /^[A-Z]$/i.test(variable);
}

function hasConcretePredictorCandidate(q) {
  if (!isPredictorConfirmationQuestion(q)) return false;

  const rawVariable = String(q?.variable || "").trim();
  const variable = rawVariable.toLowerCase();

  if (!variable) return false;

  if (isSymbolicSingleLetterVariable(rawVariable)) {
    return true;
  }

  const genericPredictorPlaceholders = new Set([
    "decision / intervention",
    "decision/intervention",
    "intervention / exposure",
    "intervention/exposure",
    "exposure / intervention",
    "exposure/intervention",
    "primary predictor",
    "predictor",
  ]);

  return !genericPredictorPlaceholders.has(variable);
}

function showsInputOnYes(q) {
  return (
    isPredictorConfirmationQuestion(q) && !hasConcretePredictorCandidate(q)
  );
}

function roleLabel(signal) {
  if (signal === "Y") return "Role: outcome (Y)";
  if (signal === "X") return "Role: candidate X";
  if (signal === "M") return "Role: mediator (M)";
  if (signal === "Z") return "Role: confounder (Z)";
  return null;
}

export default function PatCausalQnAPanel({ patDistillation, onComplete }) {
  if (!patDistillation || !Array.isArray(patDistillation.review_questions)) {
    return null;
  }

  const questions = patDistillation.review_questions;

  const [responses, setResponses] = useState(() => {
    const init = {};
    questions.forEach((q) => {
      init[q.id] = "not_sure";
    });
    return init;
  });

  const [draftInputs, setDraftInputs] = useState(() => {
    const init = {};
    questions.forEach((q) => {
      init[q.id] = "";
    });
    return init;
  });

  const [detailItems, setDetailItems] = useState(() => {
    const init = {};
    questions.forEach((q) => {
      init[q.id] = [];
    });
    return init;
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const setResponse = (id, value) => {
    const question = questions.find((q) => q.id === id);
    const replacementOnNo = usesReplacementOnNo(question);

    setResponses((prev) => ({ ...prev, [id]: value }));

    const shouldClear = isPredictorConfirmationQuestion(question)
      ? !(
          (value === "yes" && !hasConcretePredictorCandidate(question)) ||
          value === "no"
        )
      : isMediatorConfirmationQuestion(question)
        ? hasConcreteMediatorCandidate(question)
          ? value !== "no"
          : value !== "yes"
        : isConfounderConfirmationQuestion(question)
          ? hasConcreteConfounderCandidate(question)
            ? value !== "no"
            : value !== "yes"
          : replacementOnNo
            ? value !== "no"
            : value !== "yes";

    if (shouldClear) {
      setDraftInputs((prev) => ({ ...prev, [id]: "" }));
      setDetailItems((prev) => ({ ...prev, [id]: [] }));
    }
  };

  const setDraftInput = (id, value) => {
    setDraftInputs((prev) => ({ ...prev, [id]: value }));
  };

  const addDetailItem = (id) => {
    const candidate = String(draftInputs[id] || "").trim();
    if (!candidate) return;

    setDetailItems((prev) => ({
      ...prev,
      [id]: dedupeAtomList([...(prev[id] || []), candidate]),
    }));

    setDraftInputs((prev) => ({ ...prev, [id]: "" }));
  };

  const removeDetailItem = (id, index) => {
    setDetailItems((prev) => ({
      ...prev,
      [id]: (prev[id] || []).filter((_, i) => i !== index),
    }));
  };

  const allAnswered =
    questions.length > 0 &&
    questions.every((q) => ["yes", "no", "not_sure"].includes(responses[q.id]));

  const yLockout = questions.some((q) => {
    if (!isOutcomeConfirmationQuestion(q)) return false;
    if (responses[q.id] !== "no") return false;

    const replacementY = buildCommittedItemsWithDraft(
      detailItems[q.id],
      draftInputs[q.id],
    );

    return replacementY.length === 0;
  });

  const xLockout = questions.some((q) => {
    if (!isPredictorConfirmationQuestion(q)) return false;

    const response = responses[q.id];

    if (response === "yes" && !hasConcretePredictorCandidate(q)) {
      const enteredX = buildCommittedItemsWithDraft(
        detailItems[q.id],
        draftInputs[q.id],
      );

      return enteredX.length === 0;
    }

    if (response === "no") {
      const replacementX = buildCommittedItemsWithDraft(
        detailItems[q.id],
        draftInputs[q.id],
      );

      return replacementX.length === 0;
    }

    return false;
  });

  const zLockout = questions.some((q) => {
    if (!isConfounderConfirmationQuestion(q)) return false;
    if (hasConcreteConfounderCandidate(q)) return false;

    const response = responses[q.id];

    if (response !== "yes") return false;

    const enteredZ = buildCommittedItemsWithDraft(
      detailItems[q.id],
      draftInputs[q.id],
    );

    return enteredZ.length === 0;
  });

  const userPopulation = useMemo(() => {
    const populated = { X: [], M: [], Z: [], Y: [] };

    questions.forEach((q) => {
      const signal = questionPrimarySignal(q);

      if (!["X", "M", "Z", "Y"].includes(signal)) return;

      const response = responses[q.id];

      if (usesReplacementOnNo(q)) {
        if (response === "yes") {
          if (isPredictorConfirmationQuestion(q)) {
            const committedX = hasConcretePredictorCandidate(q)
              ? buildCommittedItemsForQuestion(
                  q,
                  detailItems[q.id],
                  draftInputs[q.id],
                )
              : buildCommittedItemsWithDraft(
                  detailItems[q.id],
                  draftInputs[q.id],
                );

            if (committedX.length > 0) {
              populated[signal].push(...committedX);
            }

            return;
          }
          if (isMediatorConfirmationQuestion(q)) {
            const committedM = hasConcreteMediatorCandidate(q)
              ? buildCommittedItemsForQuestion(
                  q,
                  detailItems[q.id],
                  draftInputs[q.id],
                )
              : buildCommittedItemsWithDraft(
                  detailItems[q.id],
                  draftInputs[q.id],
                );

            if (committedM.length > 0) {
              populated[signal].push(...committedM);
            }

            return;
          }

          if (isConfounderConfirmationQuestion(q)) {
            const committedZ = hasConcreteConfounderCandidate(q)
              ? buildCommittedItemsForQuestion(
                  q,
                  detailItems[q.id],
                  draftInputs[q.id],
                )
              : buildCommittedItemsWithDraft(
                  detailItems[q.id],
                  draftInputs[q.id],
                );

            if (committedZ.length > 0) {
              populated[signal].push(...committedZ);
            }

            return;
          }

          const fallbackVariable = String(q?.variable || "").trim();
          if (fallbackVariable) populated[signal].push(fallbackVariable);
        }

        if (response === "no") {
          const replacement = buildCommittedItemsWithDraft(
            detailItems[q.id],
            draftInputs[q.id],
          );

          if (replacement.length > 0) {
            populated[signal].push(...replacement);
          }
        }

        return;
      }
      if (
        isMediatorConfirmationQuestion(q) &&
        !hasConcreteMediatorCandidate(q)
      ) {
        if (response !== "yes") return;

        const nominatedM = buildCommittedItemsWithDraft(
          detailItems[q.id],
          draftInputs[q.id],
        );

        if (nominatedM.length > 0) {
          populated.M.push(...nominatedM);
        }

        return;
      }

      if (
        isConfounderConfirmationQuestion(q) &&
        !hasConcreteConfounderCandidate(q)
      ) {
        if (response !== "yes") return;

        const nominatedZ = buildCommittedItemsWithDraft(
          detailItems[q.id],
          draftInputs[q.id],
        );

        if (nominatedZ.length > 0) {
          populated.Z.push(...nominatedZ);
        }

        return;
      }

      if (response !== "yes") return;

      const committed = buildCommittedItemsForQuestion(
        q,
        detailItems[q.id],
        draftInputs[q.id],
      );

      if (committed.length > 0) {
        populated[signal].push(...committed);
      }
    });

    return {
      X: dedupeAtomList(populated.X),
      M: dedupeAtomList(populated.M),
      Z: dedupeAtomList(populated.Z),
      Y: dedupeAtomList(populated.Y),
    };
  }, [questions, responses, detailItems, draftInputs]);

  const handleConfirm = async () => {
    if (!allAnswered || yLockout || xLockout || zLockout || isSubmitting)
      return;

    setIsSubmitting(true);

    try {
      const selectedOutcome =
        dedupeAtomList(userPopulation.Y)[0] ||
        questions
          .filter(
            (q) =>
              isOutcomeConfirmationQuestion(q) && responses[q.id] === "yes",
          )
          .map((q) => String(q.variable || "").trim())
          .find(Boolean) ||
        null;

      await onComplete({
        answers: responses,
        user_population: userPopulation,
        selected_outcome: selectedOutcome,
        detail_inputs: draftInputs,
        detail_items: questions.reduce((acc, q) => {
          const signal = questionPrimarySignal(q);

          if (!["X", "M", "Z", "Y"].includes(signal)) {
            acc[q.id] = Array.isArray(detailItems[q.id])
              ? detailItems[q.id]
              : [];
            return acc;
          }

          if (usesReplacementOnNo(q) && responses[q.id] === "no") {
            acc[q.id] = buildCommittedItemsWithDraft(
              detailItems[q.id],
              draftInputs[q.id],
            );
            return acc;
          }

          if (
            isConfounderConfirmationQuestion(q) &&
            !hasConcreteConfounderCandidate(q) &&
            responses[q.id] === "yes"
          ) {
            acc[q.id] = buildCommittedItemsWithDraft(
              detailItems[q.id],
              draftInputs[q.id],
            );
            return acc;
          }

          if (responses[q.id] !== "yes") {
            acc[q.id] = Array.isArray(detailItems[q.id])
              ? detailItems[q.id]
              : [];
            return acc;
          }

          acc[q.id] = buildCommittedItemsForQuestion(
            q,
            detailItems[q.id],
            draftInputs[q.id],
          );
          return acc;
        }, {}),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="rounded-lg border border-indigo-600 bg-slate-900 p-5 space-y-6">
      <h3 className="text-sm font-semibold text-indigo-300">
        Causal Q&A (PAT-guided)
      </h3>

      <div className="text-gray-400">
        These prompts are advisory. They are designed to surface causal-model
        cautions such as candidate causes or interventions, mediators, and
        confounders. They do not determine the final variable roles.
      </div>

      <div className="space-y-4">
        {questions.map((q) => {
          const signal = questionPrimarySignal(q);
          const showConditionalInput =
            questionTargetsUserPopulation(q) &&
            (isPredictorConfirmationQuestion(q)
              ? responses[q.id] === "no" ||
                (responses[q.id] === "yes" && !hasConcretePredictorCandidate(q))
              : isMediatorConfirmationQuestion(q)
                ? hasConcreteMediatorCandidate(q)
                  ? responses[q.id] === "no"
                  : responses[q.id] === "yes"
                : isConfounderConfirmationQuestion(q)
                  ? hasConcreteConfounderCandidate(q)
                    ? responses[q.id] === "no"
                    : responses[q.id] === "yes"
                  : usesReplacementOnNo(q)
                    ? responses[q.id] === "no"
                    : responses[q.id] === "yes");

          return (
            <div
              key={q.id}
              className="border border-slate-700 rounded-md p-3 space-y-3"
            >
              <div className="text-xs text-slate-200">
                {q.text || q.prompt || "(missing question text)"}
              </div>

              <div className="flex gap-6 pl-2 text-xs text-slate-300">
                {["yes", "no", "not_sure"].map((opt) => (
                  <label key={opt} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={q.id}
                      checked={responses[q.id] === opt}
                      onChange={() => setResponse(q.id, opt)}
                    />
                    <span className="text-[11px]">{opt.replace("_", " ")}</span>
                  </label>
                ))}
              </div>

              {showConditionalInput && (
                <div className="space-y-2 pl-2">
                  <div className="text-[11px] text-slate-400 leading-relaxed">
                    {buildConditionalPrompt(signal)}
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      value={draftInputs[q.id]}
                      onChange={(e) => setDraftInput(q.id, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addDetailItem(q.id);
                        }
                      }}
                      placeholder={
                        signal === "Y"
                          ? "Add one replacement outcome"
                          : signal === "Z"
                            ? "Add one possible confounder"
                            : signal === "M"
                              ? "Add one possible mediator"
                              : "Add one proposed X variable"
                      }
                      className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-2 text-xs text-slate-200"
                    />

                    <button
                      type="button"
                      onClick={() => addDetailItem(q.id)}
                      className="text-xs text-sky-400 hover:underline"
                    >
                      + add
                    </button>
                  </div>

                  {Array.isArray(detailItems[q.id]) &&
                    detailItems[q.id].length > 0 && (
                      <ul className="space-y-1">
                        {detailItems[q.id].map((item, i) => (
                          <li
                            key={`${q.id}-${i}`}
                            className="flex items-center gap-2"
                          >
                            <div className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-2 text-xs text-slate-200">
                              {item}
                            </div>

                            <button
                              type="button"
                              onClick={() => removeDetailItem(q.id, i)}
                              className="text-xs text-slate-400 hover:text-slate-200"
                            >
                              ✕
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                </div>
              )}

              {isOutcomeConfirmationQuestion(q) && responses[q.id] === "no" && (
                <div className="pl-2 text-[11px] text-amber-300 leading-relaxed">
                  PAT can proceed only with one nominated outcome (Y). You have
                  rejected the current Y. Add one replacement outcome, or
                  re-select yes or not sure before continuing.
                </div>
              )}

              {isPredictorConfirmationQuestion(q) &&
                responses[q.id] === "no" && (
                  <div className="pl-2 text-[11px] text-amber-300 leading-relaxed">
                    You have rejected the currently surfaced X candidate. Add
                    one or more replacement candidate X variables if they should
                    be considered, or re-select yes or not sure before
                    continuing.
                  </div>
                )}

              {isMediatorConfirmationQuestion(q) &&
                hasConcreteMediatorCandidate(q) &&
                responses[q.id] === "no" && (
                  <div className="pl-2 text-[11px] text-amber-300 leading-relaxed">
                    You have rejected the currently surfaced mediator candidate.
                    Add a replacement mediator if one should be considered, or
                    re-select yes or not sure before continuing.
                  </div>
                )}

              {roleLabel(signal) && (
                <div className="text-[11px] text-slate-500 italic">
                  {q.variable ? `Selected variable: ${q.variable} | ` : ""}
                  {roleLabel(signal)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end pt-3 border-t border-slate-700">
        <button
          type="button"
          disabled={
            !allAnswered || yLockout || xLockout || zLockout || isSubmitting
          }
          onClick={handleConfirm}
          className={`px-4 py-2 text-xs rounded font-semibold transition-all duration-200 ${
            isSubmitting
              ? "bg-emerald-500 text-slate-950 animate-pulse"
              : "bg-indigo-500 text-slate-900"
          } disabled:opacity-40`}
        >
          {isSubmitting
            ? "Submitting responses..."
            : "Confirm responses and continue"}
        </button>
      </div>
    </div>
  );
}
